import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import {
  CountryFlag,
  Input,
  MultiSelectFilter,
  SimpleSelect,
} from '@/shared/ui';
import {
  OFFER_STATUS_FILTER_OPTIONS,
  OFFER_STATUS_I18N_KEY,
} from '@entities/Offer';
import type { AvailabilityStatus, TrafficType } from '@psp-aggregator/sdk';

const TRAFFIC_TYPES: TrafficType[] = [
  'trusted',
  'ftd',
  'nftd',
  'incentive',
  'grey',
  'quasi_ecom',
];

const TRAFFIC_LABEL: Record<TrafficType, string> = {
  ftd: 'FTD',
  grey: 'Grey',
  incentive: 'Incentive',
  nftd: 'NFTD',
  quasi_ecom: 'Quasi-ecom',
  trusted: 'Trusted',
};

export interface OffersFilterValues {
  countries: string[];
  currencies: string[];
  integrationMethods: string[];
  providerIds: string[];
  sort: 'newest' | 'oldest';
  statuses: AvailabilityStatus[];
  trafficTypes: TrafficType[];
}

export interface OffersFiltersProps {
  countries: string[];
  currencies: string[];
  integrations: string[];
  providers: { label: string; value: string }[];
  search: string;
  values: OffersFilterValues;
  onChange: (values: OffersFilterValues) => void;
  onSearchChange: (value: string) => void;
}

export const OffersFilters: FC<OffersFiltersProps> = ({
  countries,
  currencies,
  integrations,
  onChange,
  onSearchChange,
  providers,
  search,
  values,
}) => {
  const { t } = useTranslation();
  const patch = (next: Partial<OffersFilterValues>): void => {
    onChange({ ...values, ...next });
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label={t('pages.admin.offers.searchPlaceholder')}
          className="h-9 w-44 rounded-full bg-background/60 pl-8"
          placeholder={t('pages.admin.offers.searchPlaceholder')}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      <SimpleSelect
        aria-label={t('pages.admin.offers.filters.sort')}
        options={[
          { label: t('pages.admin.offers.sort.newest'), value: 'newest' },
          { label: t('pages.admin.offers.sort.oldest'), value: 'oldest' },
        ]}
        triggerClassName="h-9 w-auto rounded-full px-3"
        value={values.sort}
        onValueChange={(sort) =>
          patch({ sort: sort === 'oldest' ? 'oldest' : 'newest' })
        }
      />
      <MultiSelectFilter
        label={t('pages.admin.offers.fields.status')}
        options={OFFER_STATUS_FILTER_OPTIONS.map((status) => ({
          label: t(OFFER_STATUS_I18N_KEY[status]),
          value: status,
        }))}
        selected={values.statuses}
        onChange={(statuses) =>
          patch({ statuses: statuses as AvailabilityStatus[] })
        }
      />
      <MultiSelectFilter
        label={t('pages.admin.offers.filters.provider')}
        options={providers}
        selected={values.providerIds}
        onChange={(providerIds) => patch({ providerIds })}
      />
      <MultiSelectFilter
        label={t('pages.admin.offers.filters.geo')}
        options={countries.map((code) => ({
          icon: <CountryFlag code={code} />,
          label: code,
          value: code,
        }))}
        selected={values.countries}
        onChange={(next) => patch({ countries: next })}
      />
      <MultiSelectFilter
        label={t('pages.admin.offers.filters.currency')}
        options={currencies.map((code) => ({ label: code, value: code }))}
        selected={values.currencies}
        onChange={(next) => patch({ currencies: next })}
      />
      <MultiSelectFilter
        label={t('pages.admin.offers.fields.trafficType')}
        options={TRAFFIC_TYPES.map((type) => ({
          label: TRAFFIC_LABEL[type],
          value: type,
        }))}
        selected={values.trafficTypes}
        onChange={(trafficTypes) =>
          patch({ trafficTypes: trafficTypes as TrafficType[] })
        }
      />
      <MultiSelectFilter
        label={t('pages.admin.offers.filters.integration')}
        options={integrations.map((code) => ({ label: code, value: code }))}
        selected={values.integrationMethods}
        onChange={(integrationMethods) => patch({ integrationMethods })}
      />
    </div>
  );
};

OffersFilters.displayName = 'OffersFilters';
